import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Basis } from '~/models/studio';
import { assert } from '~/util';
import SimWorker from './simulation-worker?worker';
import type {
  MessageToHost,
  MessageToWorker,
  Run,
  SimResult
} from './simulation-worker';
import type { ReactNode } from 'react';
import type { Vec2 } from '~/util';

export type { SimResult };

export class ErrSimDataStale extends Error {
  constructor() {
    super();
    this.message = 'Simulation Data Stale!';
  }
}

export interface SimProgress {
  ticksDone: number;
  done: boolean;
}

export interface SimulationState {
  startSim: (register: Vec2[], runs: Run[]) => void;
  requestData: (
    time: number
  ) => Promise<{ result: SimResult; simNonce: number }>;
  progress: SimProgress;
}

const SimulationContext = createContext<SimulationState | null>(null);
SimulationContext.displayName = 'Simulation';

export function SimulationProvider({ children }: { children: ReactNode }) {
  const workerRef = useRef<Worker | null>(null);
  const [progress, setProgress] = useState<SimProgress>({
    ticksDone: 0,
    done: false
  });
  const currentSimNonceRef = useRef(0);
  const nextDataNonceRef = useRef(0);
  const dataResolversRef = useRef(
    new Map<number, (result: SimResult | null) => void>()
  );

  useEffect(() => {
    const worker = new SimWorker() as Worker;
    workerRef.current = worker;

    worker.addEventListener('message', (event) => {
      const msg = event.data as MessageToHost;
      switch (msg.kind) {
        case 'progress':
          if (msg.simNonce === currentSimNonceRef.current) {
            setProgress({ ticksDone: msg.ticksDone, done: msg.done });
          }
          break;
        case 'data': {
          const resolver = dataResolversRef.current.get(msg.dataNonce);
          if (resolver) {
            resolver(msg.result);
            dataResolversRef.current.delete(msg.dataNonce);
          }
          break;
        }
      }
    });

    return () => {
      clearResolvers();
      worker.terminate();
    };
  }, []);

  function postMsg(msg: MessageToWorker) {
    workerRef.current?.postMessage(msg);
  }

  function clearResolvers() {
    for (const req of dataResolversRef.current.values()) {
      req(null);
    }
    dataResolversRef.current.clear();
  }

  function reversePulseAddressing(register: Vec2[], runs: Run[]) {
    const nbAtoms = register.length;
    return runs.map((run) => ({
      ...run,
      address: run.address === null ? null : nbAtoms - run.address - 1
    }));
  }

  const startSim = useCallback((register: Vec2[], runs: Run[]) => {
    currentSimNonceRef.current += 1;
    clearResolvers();
    postMsg({
      kind: 'simulate',
      simNonce: currentSimNonceRef.current,
      register,
      runs: reversePulseAddressing(register, runs)
    });
  }, []);

  const requestData = useCallback(
    (time: number): Promise<{ result: SimResult; simNonce: number }> => {
      const dataNonce = nextDataNonceRef.current++;
      return new Promise((resolve, reject) => {
        const simNonce = currentSimNonceRef.current;
        dataResolversRef.current.set(dataNonce, (result) => {
          if (result != null) {
            resolve({ result, simNonce });
          } else {
            reject(new ErrSimDataStale());
          }
        });
        postMsg({
          kind: 'getData',
          simNonce,
          dataNonce,
          timestep: time
        });
      });
    },
    []
  );

  const contextValue = useMemo(
    () => ({ startSim, requestData, progress }),
    [startSim, requestData, progress]
  );

  return (
    <SimulationContext.Provider value={contextValue}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation(): SimulationState {
  const ctx = useContext(SimulationContext);
  if (!ctx) {
    throw new Error('Simulation not provided');
  }
  return ctx;
}

export interface SimulatedComponent {
  qutritIndices: bigint; // which state each atom is in
  re: number; // real part of amplitude
  im: number; // imaginary part of amplitude
  mag2: number; // squared magnitude (probability = re² + im²)
}

/** Decode simulation result binary data into a JS array of components */
export function decodeSimulatedData(res: SimResult): SimulatedComponent[] {
  const useWideIndices = res.indexSize > 16;

  const numComponents = useWideIndices
    ? res.indices.length / 2
    : res.indices.length;
  assert(numComponents * 2 === res.values.length);

  const components: SimulatedComponent[] = new Array(numComponents);

  for (let i = 0; i < numComponents; i++) {
    const qutritIndices = useWideIndices
      ? BigInt(res.indices[i * 2]) | (BigInt(res.indices[i * 2 + 1]) << 32n)
      : BigInt(res.indices[i]);
    const re = res.values[i * 2];
    const im = res.values[i * 2 + 1];
    components[i] = {
      qutritIndices,
      re,
      im,
      mag2: re * re + im * im
    };
  }

  if (import.meta.env.DEV) {
    for (let i = 1; i < numComponents; i++) {
      const c0 = components[i - 1];
      const c1 = components[i];
      assert(
        c0.qutritIndices < c1.qutritIndices,
        `qutritIndices not in order: [${i - 1}] ${c0.qutritIndices} < [${i}] ${c1.qutritIndices}`
      );
    }
  }
  return components;
}

export interface MeasuredComponent {
  qubitIndices: number; // which state each atom is in
  mag2: number; // squared magnitude (probability)
}

/** Calculate measurement in specific basis based on simulation data */
export function measure(
  components: SimulatedComponent[],
  basis: Basis
): MeasuredComponent[] {
  const maskH = 0x5555555555555555n;
  const maskR = 0xaaaaaaaaaaaaaaaan;
  const mapIndex =
    basis === Basis.Digital
      ? (idx: bigint) => idx & maskH
      : (idx: bigint) => (idx & maskR) >> 1n;

  const getOddBits = (idx: bigint): number => {
    let odd = 0;
    for (let i = 0; idx !== 0n; i++) {
      odd |= Number(idx & 1n) << i;
      idx >>= 2n;
    }
    return odd;
  };

  const indexMap = new Map<bigint, number>();
  for (let i = 0; i < components.length; i++) {
    const c = components[i];
    const idx = mapIndex(c.qutritIndices);
    const existing = indexMap.get(idx);
    indexMap.set(idx, c.mag2 + (existing ?? 0));
  }
  return Array.from(indexMap.entries(), (entry) => ({
    qubitIndices: getOddBits(entry[0]),
    mag2: entry[1]
  }));
}
